// packages import
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Autocomplete, Box, FormControl, Grid, TextField, Typography, createFilterOptions } from '@mui/material'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
//others
import { CaseType, LawType, Maybe } from '../../../../../generated'
import { CaseContext } from '../../../../context'
import { FormErrors, LawTypeProps } from '../../../../interfaceTypes'
import { menuHeight } from '../../../../theme/styleConstants'

export const LawTypes: FC<LawTypeProps> = ({ isDisable, currentLawTypeId }) => {
  const { allLawTypes } = useContext(CaseContext)
  const [lawType, setLawType] = useState<Maybe<LawType>>()
  const [lawTypeId, setLawTypeId] = useState<string>()
  // A pre-selected law type (edit, or a duplicated matter) already has its matter types to pick from.
  const [disabled, setDisabled] = useState<boolean>(!currentLawTypeId)
  const {
    control,
    setValue,
    formState: { errors },
    watch
  } = useFormContext()
  const isConsultMatter = watch('isConsultMatter')
  const selectedLawType = allLawTypes?.find(item => item?.id === lawTypeId)
  const lawTypeCustomError = errors['lawType'] as FormErrors['lawType']
  const caseTypeCustomError = errors['caseType'] as FormErrors['caseType']
  const autoComplete = useRef(null)
  const noCaseType = selectedLawType?.caseTypes?.length === 0

  useEffect(() => {
    if (lawType) {
      setLawTypeId(lawType.id)
    } else if (currentLawTypeId) {
      setLawTypeId(currentLawTypeId)
    }
  }, [currentLawTypeId, lawType])

  useEffect(() => {
    if (isConsultMatter && !lawTypeId && !currentLawTypeId) {
      const defaultLawType = allLawTypes?.find(item => item?.name === 'Consult')
      if (defaultLawType) {
        const defaultCaseType = defaultLawType?.caseTypes?.find(item => item?.name === 'Consult')
        setValue('caseType', defaultCaseType)
      }
      setValue('lawType', defaultLawType)
    }
  }, [allLawTypes])

  return (
    <Grid container columnSpacing={2.5} rowSpacing={{ xs: 2.5, md: 0 }} alignItems={noCaseType ? 'center' : ''}>
      <Grid item md={6} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth>
          <Controller
            name="lawType"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              return (
                <Autocomplete
                  fullWidth
                  disableClearable
                  options={allLawTypes || []}
                  onChange={(event, item) => {
                    onChange(item)
                    setLawType(item)
                    setValue('caseType', { id: '', label: '' })
                    setDisabled(false)
                  }}
                  ListboxProps={menuHeight}
                  getOptionLabel={value => value?.name || ''}
                  filterOptions={createFilterOptions({ ignoreCase: true })}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id
                  }}
                  value={value}
                  renderInput={params => <TextField {...params} error={!!lawTypeCustomError?.id?.message} helperText={lawTypeCustomError?.id?.message || ''} label="Law Type" />}
                />
              )
            }}
          />
        </FormControl>
      </Grid>

      <Grid item md={6} sm={12} xs={12}>
        {noCaseType ? (
          <Box display="flex" gap="5px" alignItems="center">
            <InfoOutlinedIcon fontSize="small" color="error" />
            <Typography sx={{ fontSize: 12 }} color="error">
              Please add a matter type first to create matter
            </Typography>
          </Box>
        ) : (
          <FormControl variant="outlined" fullWidth>
            <Controller
              name="caseType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  fullWidth
                  value={value}
                  disableClearable
                  disabled={isDisable ? disabled : false}
                  filterOptions={createFilterOptions({ ignoreCase: true })}
                  ref={autoComplete}
                  getOptionLabel={option => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  options={[{ id: '', name: '' } as Maybe<CaseType>, ...(selectedLawType?.caseTypes || [])]}
                  onChange={(event, item) => {
                    onChange(item)
                  }}
                  ListboxProps={menuHeight}
                  renderInput={params => <TextField {...params} error={!!caseTypeCustomError?.id?.message} helperText={caseTypeCustomError?.id?.message || ''} label="Matter Type" />}
                />
              )}
            />
          </FormControl>
        )}
      </Grid>
    </Grid>
  )
}
